import { useEffect, useRef, useState } from 'react'

const WaveformCanvas = ({
  playerRef,
  waveformData,
  theme,
  onMouseMove = () => {},
  onMouseDown = () => {},
  onMouseUp = () => {},
  onClick = () => {},
}) => {
  const containerRef = useRef(null)
  const requestIdRef = useRef(null)
  const canvasRef = useRef(null)
  const mouseRef = useRef({
    x: 0,
    down: false,
  })
  const shouldDrawRef = useRef(0)
  const player = playerRef.current

  const tick = () => {
    if (!canvasRef.current) {
      return
    }
    drawFrame()

    requestIdRef.current = requestAnimationFrame(tick)
  }

  useEffect(() => {
    requestIdRef.current = requestAnimationFrame(tick)
    return () => {
      cancelAnimationFrame(requestIdRef.current)
    }
  }, [theme, waveformData, playerRef.current.trackId])

  function handleMouseMove(e) {
    const canvasWidth = canvasRef.current.clientWidth
    const offsetX = e.nativeEvent.offsetX
    if (onMouseMove) {
      onMouseMove(offsetX / canvasWidth)
    }
    mouseRef.current.x = offsetX
  }

  function handleMouseLeave(evt) {
    const offsetX = evt.nativeEvent.offsetX
    const canvasWidth = canvasRef.current.clientWidth
    if (mouseRef.current.down && (offsetX < 0 || offsetX > canvasWidth - 10)) {
      handleMouseUp(evt)
    }
    mouseRef.current.x = 0
  }

  function handleMouseDown() {
    const canvasWidth = canvasRef.current.clientWidth
    mouseRef.current.down = true
    onMouseDown(mouseRef.current.x / canvasWidth)
  }

  function handleMouseUp(evt) {
    const offsetX = evt.nativeEvent.offsetX
    const canvasWidth = canvasRef.current.clientWidth
    mouseRef.current.down = false
    onMouseUp(offsetX / canvasWidth)
  }

  function drawFrame() {
    const canvas = canvasRef.current
    const width = canvas.parentElement.clientWidth
    const height = canvas.parentElement.clientHeight
    const ctx = canvas.getContext('2d')
    const currentTime =
      player.audioContext && player.buffer
        ? player.ellapsedTime +
          player.audioContext.currentTime -
          player.startTime
        : 0
    const duration = player.duration
    const timeProgress = currentTime / duration
    const mousePosition = mouseRef.current.x
    const hovered = mousePosition > 0
    const mousePositionRelative = mousePosition / width
    const mouseAhead = mousePositionRelative > timeProgress
    const analyzer = player.analyzer
    const isPlaying = player.state !== 'paused'
    canvas.width = width
    canvas.height = height
    const { bgColor, fgColor, highlightColor, visualizer } = theme

    ctx.clearRect(0, 0, 1500, 500)
    if (currentTime > 0) {
      let dataArray = []
      let visualizerArray = []
      if (analyzer) {
        const bufferLength = analyzer.frequencyBinCount
        dataArray = new Uint8Array(bufferLength)
        analyzer.getByteFrequencyData(dataArray)
        visualizerArray = dataArray.slice(0, 1700)
      }

      drawWaveform(
        canvas,
        0,
        1,
        {
          color: bgColor,
          visualizer,
        },
        visualizerArray,
        !!analyzer && isPlaying,
      )
      drawWaveform(
        canvas,
        0,
        timeProgress,
        {
          color: hovered && !mouseAhead ? fgColor : highlightColor,
          visualizer,
        },
        visualizerArray,
      )

      if (mousePosition > 0) {
        let color = highlightColor
        let startPosition = 0
        if (mouseAhead) {
          startPosition = timeProgress
          color = fgColor
        }

        drawWaveform(
          canvas,
          startPosition,
          mousePositionRelative,
          {
            color,
            visualizer,
          },
          visualizerArray,
        )
      }
    } else {
      drawWaveform(canvas, 0, 0.4, {
        color: highlightColor,
        visualizer,
      })

      drawWaveform(canvas, 0.4, 0.7, {
        color: fgColor,
        visualizer,
      })

      drawWaveform(canvas, 0.7, 1, {
        color: bgColor,
        visualizer,
      })
    }
  }

  function drawWaveform(
    canvas,
    startPosition,
    endPosition,
    theme,
    freqPoints = [],
    writeToWindow,
  ) {
    const data = waveformData
    const ctx = canvas.getContext('2d')
    const width = canvas.clientWidth
    const offset = 75.5
    const timeFactor = width / data.length
    const drawStart = Math.floor(data.length * startPosition)
    const drawEnd = Math.ceil(data.length * endPosition)
    const { color, visualizer } = theme
    ctx.beginPath()

    const top = []
    let i = 0
    for (let x = drawStart; x < drawEnd; x += 1) {
      if (data[x] !== undefined) {
        const val = Number(data[x]?.[1]) * getFrequencyModifier(x)
        const y = offset + clampWaveValue(Math.round(val))
        ctx.lineTo(x * timeFactor, y)
        if (i++ > 1) {
          top.push([x, y])
          i = 0
        }
      }
    }

    const bottom = []
    for (let x = drawEnd - 1; x >= drawStart; x -= 1) {
      if (data[x] !== undefined) {
        const val = Number(data[x]?.[0]) * getFrequencyModifier(x)
        const y = offset + clampWaveValue(Math.round(val))
        ctx.lineTo(x * timeFactor, y)
        if (i++ > 1) {
          bottom.push([x, y])
          i = 0
        }
      }
    }

    if (writeToWindow) {
      if (shouldDrawRef.current > 10) {
        if (window.waveformFrames === undefined) {
          window.waveformFrames = []
        }
        window.waveformFrames.push({
          top,
          bottom,
        })
        shouldDrawRef.current = 0
      }
      shouldDrawRef.current = shouldDrawRef.current + 1
    }

    ctx.fillStyle = color
    ctx.strokeStyle = color
    ctx.closePath()
    ctx.stroke()
    ctx.fill()

    function getFrequencyModifier(x) {
      let baseMultiplier = 70
      if (visualizer === 'default') {
        if (freqPoints.length > 0) {
          baseMultiplier = 35
          const chunkSize = Math.ceil(data.length / freqPoints.length)
          return (
            (1 + (freqPoints[Math.floor(x / chunkSize)] / 255) * 1.75) *
            baseMultiplier
          )
        }
      } else if (visualizer === 'orchestral') {
        if (freqPoints.length > 0) {
          baseMultiplier = 60
          const chunkSize = Math.ceil(data.length / freqPoints.length)
          return (
            (0.6 + (freqPoints[Math.floor(x / chunkSize)] / 255) * 1.75) *
            baseMultiplier
          )
        }
      } else if (visualizer === 'bass') {
        if (freqPoints.length > 0) {
          //freqPoints[5] is aprox 55-65hz
          baseMultiplier = 80
          const chunkSize = Math.ceil(data.length / freqPoints.length)
          return (
            (0.25 +
              freqPoints[5] / 255 +
              freqPoints[Math.floor(x / chunkSize)] / 255) *
            baseMultiplier
          )
        }
      }
      return baseMultiplier
    }
  }

  return (
    <div
      ref={containerRef}
      className="w-full absolute t-0 l-0"
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      onMouseDown={handleMouseDown}
      onMouseUp={handleMouseUp}
    >
      <canvas ref={canvasRef}></canvas>
    </div>
  )
}

export default WaveformCanvas

function easeOutQuad(t, b, c, d) {
  return -c * (t /= d) * (t - 2) + b
}

function clampWaveValue(x) {
  const sign = Math.sign(x)
  const abs = Math.abs(x) / 100
  if (abs > 0.7) {
    const value = 1 - (1 - abs) * (1 - abs)
    return sign * value * 74
  } else {
    return x
  }
}

function sum(arr) {
  return arr.reduce((acc, cur) => {
    return acc + cur
  }, 0)
}
